import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useAgentConfigStore } from '../../stores/agentConfigStore';

/**
 * Shared Prompt Input Bar Component
 * Used in both BuilderPromptScreen and BuilderWorkspace
 * 
 * Features:
 * - Auto-expanding textarea (up to 7 lines)
 * - Send button (or Stop button when executing)
 * - Tags for Experience ID and Workflow Preset Name (clickable to change preset)
 * - File/Image attachments with thumbnails
 */

// Constants for textarea sizing
const LINE_HEIGHT = 20;
const MIN_LINES = 1;
const MAX_LINES = 7;
const MIN_HEIGHT = LINE_HEIGHT * MIN_LINES;
const MAX_HEIGHT = LINE_HEIGHT * MAX_LINES;

// Supported file types for attachments
const ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
const ACCEPTED_DOC_TYPES = ['application/pdf', 'text/plain', 'text/markdown', 'text/html', 'text/csv'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

function PromptInputBar({
  value,
  onChange,
  onSubmit,
  onStop,
  onAttachmentsChange,
  isExecuting = false,
  experienceId = '24856',
  placeholder = 'Type a message...',
  disabled = false,
  dropDirection = 'up', // 'up' or 'down' - direction of preset dropdown
  projectTemplate = null // filter presets by project template ('unlimited3d', 'threejs', or null for all)
}) {
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);
  const presetDropdownRef = useRef(null);
  const [attachments, setAttachments] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [showPresetDropdown, setShowPresetDropdown] = useState(false);
  const [isLoadingPreset, setIsLoadingPreset] = useState(false);
  const dragCounter = useRef(0); // Track nested drag events

  // Get workflow state from store
  const workflowName = useAgentConfigStore((state) => state.workflowName);
  const workflowId = useAgentConfigStore((state) => state.workflowId);
  const presets = useAgentConfigStore((state) => state.presets);
  const presetsLoading = useAgentConfigStore((state) => state.presetsLoading);
  
  // Track if we've already fetched presets in this session
  const hasFetchedPresets = useRef(false);

  const prevTemplateRef = useRef(projectTemplate);
  const hasLoadedTemplateDefault = useRef(false);

  // When projectTemplate changes, load the correct default preset for that template
  useEffect(() => {
    if (projectTemplate && !hasLoadedTemplateDefault.current) {
      hasLoadedTemplateDefault.current = true;
      useAgentConfigStore.getState().loadDefaultForTemplate(projectTemplate);
    }
  }, [projectTemplate]);

  // Fetch presets when dropdown opens (only once per session, or if presets are empty)
  // Also re-fetch when projectTemplate changes (user switches projects)
  useEffect(() => {
    const templateChanged = prevTemplateRef.current !== projectTemplate;
    if (templateChanged) {
      prevTemplateRef.current = projectTemplate;
      hasFetchedPresets.current = false;
      hasLoadedTemplateDefault.current = false;
    }

    if (showPresetDropdown && !presetsLoading) {
      if (!hasFetchedPresets.current || presets.length === 0) {
        hasFetchedPresets.current = true;
        useAgentConfigStore.getState().fetchPresets(projectTemplate);
      }
    }
  }, [showPresetDropdown, presetsLoading, presets.length, projectTemplate]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (presetDropdownRef.current && !presetDropdownRef.current.contains(event.target)) {
        setShowPresetDropdown(false);
      }
    };

    if (showPresetDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPresetDropdown]);

  // Handle preset selection
  const handleSelectPreset = async (preset) => {
    if (isLoadingPreset || isExecuting) return;
    
    setIsLoadingPreset(true);
    try {
      // Get loadPreset directly from store to avoid stale closure
      await useAgentConfigStore.getState().loadPreset(preset.id);
      setShowPresetDropdown(false);
    } catch (error) {
      console.error('Failed to load preset:', error);
    } finally {
      setIsLoadingPreset(false);
    }
  };

  // Toggle preset dropdown
  const togglePresetDropdown = () => {
    if (!isExecuting) {
      setShowPresetDropdown(prev => !prev);
    }
  };

  // Auto-resize textarea based on content
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = `${MIN_HEIGHT}px`;
      const scrollHeight = textarea.scrollHeight;
      const newHeight = Math.min(Math.max(scrollHeight, MIN_HEIGHT), MAX_HEIGHT);
      textarea.style.height = `${newHeight}px`;
    }
  }, [value]);

  // Notify parent of attachment changes
  useEffect(() => {
    if (onAttachmentsChange) {
      onAttachmentsChange(attachments);
    }
  }, [attachments, onAttachmentsChange]);

  // Generate unique ID for attachments
  const generateId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  // Process files (shared between file input and drag/drop)
  const processFiles = useCallback((files) => {
    files.forEach(file => {
      // Validate file size
      if (file.size > MAX_FILE_SIZE) {
        console.warn(`File ${file.name} is too large (max ${MAX_FILE_SIZE / 1024 / 1024}MB)`);
        return;
      }

      // Determine file type category
      const isImage = ACCEPTED_IMAGE_TYPES.includes(file.type);
      const isDoc = ACCEPTED_DOC_TYPES.includes(file.type);

      if (!isImage && !isDoc) {
        console.warn(`File type ${file.type} is not supported`);
        return;
      }

      // Create file reader
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64 = event.target.result;
        
        // For images, create a preview
        const attachment = {
          id: generateId(),
          file: file,
          name: file.name,
          type: file.type,
          size: file.size,
          isImage,
          base64: base64, // Full data URL
          preview: isImage ? base64 : null,
        };

        setAttachments(prev => [...prev, attachment]);
      };

      reader.readAsDataURL(file);
    });
  }, []);

  // Handle file selection from input
  const handleFileSelect = useCallback((e) => {
    const files = Array.from(e.target.files || []);
    processFiles(files);

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [processFiles]);

  // Drag and drop handlers
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounter.current = 0;

    if (isExecuting) return;

    const files = Array.from(e.dataTransfer.files || []);
    if (files.length > 0) {
      processFiles(files);
    }
  }, [isExecuting, processFiles]);

  // Handle clipboard paste (Ctrl+V / Cmd+V)
  const handlePaste = useCallback((e) => {
    if (isExecuting) return;

    const items = e.clipboardData?.items;
    if (!items) return;

    const imageFiles = [];
    for (const item of items) {
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) imageFiles.push(file);
      }
    }

    if (imageFiles.length > 0) {
      e.preventDefault();
      processFiles(imageFiles);
    }
  }, [isExecuting, processFiles]);

  // Remove attachment
  const removeAttachment = useCallback((id) => {
    setAttachments(prev => prev.filter(a => a.id !== id));
  }, []);

  // Open file picker
  const openFilePicker = useCallback(() => {
    if (fileInputRef.current && !isExecuting) {
      fileInputRef.current.click();
    }
  }, [isExecuting]);

  // Clear attachments after submit
  const clearAttachments = useCallback(() => {
    setAttachments([]);
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if ((value.trim() || attachments.length > 0) && !disabled && !isExecuting) {
        onSubmit(clearAttachments);
      }
    }
  };

  const handleSubmit = () => {
    if ((value.trim() || attachments.length > 0) && !disabled && !isExecuting) {
      onSubmit(clearAttachments);
    }
  };

  const handleStop = () => {
    if (onStop) {
      onStop();
    }
  };

  // Get file icon based on type
  const getFileIcon = (type) => {
    if (type.includes('pdf')) return '📄';
    if (type.includes('text')) return '📝';
    if (type.includes('csv')) return '📊';
    return '📎';
  };

  return (
    <div 
      className={`prompt-input-bar ${isDragging ? 'dragging' : ''}`}
      ref={dropZoneRef}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={[...ACCEPTED_IMAGE_TYPES, ...ACCEPTED_DOC_TYPES].join(',')}
        multiple
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      {/* Drop zone overlay */}
      {isDragging && (
        <div className="prompt-drop-overlay">
          <div className="drop-overlay-content">
            <span className="drop-icon">📎</span>
            <span className="drop-text">Drop files here</span>
          </div>
        </div>
      )}

      <div className={`prompt-input-container ${isExecuting ? 'executing' : ''}`}>
        {/* Teal gradient overlay - transitions opacity independently from the conic glow */}
        <div className={`prompt-teal-overlay ${isExecuting ? 'hidden' : ''}`} />
        <div className="prompt-input-inner">
          {/* Content Area */}
          <div className="prompt-content-area">
            {/* Attachment Thumbnails */}
            {attachments.length > 0 && (
              <div className="prompt-attachments">
                {attachments.map(attachment => (
                  <div key={attachment.id} className="attachment-thumbnail">
                    {attachment.isImage ? (
                      <img 
                        src={attachment.preview} 
                        alt={attachment.name}
                        className="attachment-image"
                      />
                    ) : (
                      <div className="attachment-file">
                        <span className="attachment-file-icon">{getFileIcon(attachment.type)}</span>
                        <span className="attachment-file-name">{attachment.name}</span>
                      </div>
                    )}
                    <button 
                      className="attachment-remove"
                      onClick={() => removeAttachment(attachment.id)}
                      type="button"
                      title="Remove attachment"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Textarea Container */}
            <div className="prompt-textarea-container">
              <textarea
                ref={textareaRef}
                className="prompt-textarea"
                placeholder={isExecuting ? "" : placeholder}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                rows={1}
                disabled={isExecuting}
              />
              {isExecuting && (
                <span className="prompt-shimmer-text">Agents are working...</span>
              )}
            </div>

            {/* Bottom Row - Tags + Send/Stop Button */}
            <div className="prompt-bottom-row">
              {/* Tags */}
              <div className="prompt-tags">
                {/* Attachment Button */}
                <button 
                  className="prompt-tag-btn" 
                  type="button" 
                  disabled={isExecuting}
                  onClick={openFilePicker}
                  title="Add images or files"
                >
                  <img src="/assets/builder/attachment.svg" alt="" />
                  {attachments.length > 0 && (
                    <span className="attachment-count">{attachments.length}</span>
                  )}
                </button>

                {/* Experience ID Tag */}
                <div className="prompt-tag experience" title={experienceId}>
                  <span className="tag-text">
                    Experience ID {experienceId.length > 12 
                      ? `${experienceId.substring(0, 8)}...` 
                      : experienceId
                    }
                  </span>
                </div>

                {/* Workflow Preset Selector */}
                <div className="prompt-tag-dropdown-container" ref={presetDropdownRef}>
                  <button 
                    className={`prompt-tag model clickable ${showPresetDropdown ? 'active' : ''}`}
                    onClick={togglePresetDropdown}
                    disabled={isExecuting}
                    type="button"
                    title="Change workflow preset"
                  >
                  <img src="/assets/builder/julian-new.svg" alt="" className="tag-icon" />
                  <span className="tag-text">{workflowName || 'Loading...'}</span>
                    <svg 
                      className={`tag-chevron ${showPresetDropdown ? 'open' : ''}`}
                      width="12" 
                      height="12" 
                      viewBox="0 0 12 12" 
                      fill="none"
                    >
                      <path 
                        d={dropDirection === 'up' ? "M3 7.5L6 4.5L9 7.5" : "M3 4.5L6 7.5L9 4.5"} 
                        stroke="currentColor" 
                        strokeWidth="1.5" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>

                  {/* Preset Dropdown */}
                  {showPresetDropdown && (
                    <div className={`preset-dropdown ${dropDirection}`}>
                      <div className="preset-dropdown-header">
                        <span>Select Workflow Preset</span>
                      </div>
                      
                      {presetsLoading ? (
                        <div className="preset-dropdown-loading">
                          <div className="preset-loading-spinner" />
                          <span>Loading presets...</span>
                        </div>
                      ) : presets.length === 0 ? (
                        <div className="preset-dropdown-empty">
                          No presets available
                        </div>
                      ) : (
                        <div className="preset-dropdown-list">
                          {/* System Presets */}
                          {presets.filter(p => p.is_system).length > 0 && (
                            <>
                              <div className="preset-dropdown-section">System Presets</div>
                              {presets.filter(p => p.is_system).map(preset => (
                                <button
                                  key={preset.id}
                                  className={`preset-dropdown-item ${preset.id === workflowId ? 'active' : ''}`}
                                  onClick={() => handleSelectPreset(preset)}
                                  disabled={isLoadingPreset}
                                >
                                  <div className="preset-item-info">
                                    <span className="preset-item-name">{preset.name}</span>
                                    {preset.description && (
                                      <span className="preset-item-desc">{preset.description}</span>
                                    )}
                                  </div>
                                  {preset.id === workflowId && (
                                    <svg className="preset-item-check" width="14" height="14" viewBox="0 0 14 14" fill="none">
                                      <path d="M2.5 7L5.5 10L11.5 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                  )}
                                </button>
                              ))}
                            </>
                          )}
                          
                          {/* User Presets */}
                          {presets.filter(p => !p.is_system).length > 0 && (
                            <>
                              <div className="preset-dropdown-section">My Presets</div>
                              {presets.filter(p => !p.is_system).map(preset => (
                                <button
                                  key={preset.id}
                                  className={`preset-dropdown-item ${preset.id === workflowId ? 'active' : ''}`}
                                  onClick={() => handleSelectPreset(preset)}
                                  disabled={isLoadingPreset}
                                >
                                  <div className="preset-item-info">
                                    <span className="preset-item-name">{preset.name}</span>
                                    {preset.description && (
                                      <span className="preset-item-desc">{preset.description}</span>
                                    )}
                                  </div>
                                  {preset.id === workflowId && (
                                    <svg className="preset-item-check" width="14" height="14" viewBox="0 0 14 14" fill="none">
                                      <path d="M2.5 7L5.5 10L11.5 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                  )}
                                </button>
                              ))}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Send or Stop Button */}
              {isExecuting ? (
                <button
                  className="prompt-stop-btn"
                  onClick={handleStop}
                  type="button"
                  title="Stop execution"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <rect x="3" y="3" width="10" height="10" rx="1" fill="currentColor" />
                  </svg>
                </button>
              ) : (
                <button
                  className="prompt-send-btn"
                  onClick={handleSubmit}
                  disabled={(!value.trim() && attachments.length === 0) || disabled}
                  type="button"
                >
                  <img src="/assets/builder/send-arrow.svg" alt="Send" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PromptInputBar;
